<script setup lang="ts">
import { EllipsisVerticalIcon, NoSymbolIcon, UserPlusIcon } from '@heroicons/vue/20/solid'
import { UserRoleId } from '@/enums.ts'
import router from '@/router.ts'
import { useMutation, useQuery } from '@vue/apollo-composable'
import * as I from '@/components/users/interfaces.ts'
import { isEmpty, isNull, isUndefined, sortBy } from 'lodash'
import { FindUsersQuery } from '@/components/users/graphql/user.queries.ts'
import {
  ReleaseUserMutation,
  RestrictUserMutation,
} from '@/components/users/graphql/user.mutations.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { computed } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import type { User } from '@/components/users/interfaces.ts'
import { useToast } from 'vue-toastification'
import CrdBlock from '@/components/common/CrdBlock.vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import CrdWrapper from '@/components/common/CrdWrapper.vue'
import dayjs from 'dayjs'

const route = useRoute()
const { t } = useTranslations()
const toast = useToast()
const userStore = useUserStore()
const { projectSelected } = storeToRefs(userStore)
const {
  result: data,
  onError: onUsersLoadingFailed,
  loading: isLoading,
} = useQuery<{ users: I.User[] }>(FindUsersQuery, null, {
  fetchPolicy: 'cache-and-network',
})
const {
  onDone: onReleaseUserDone,
  onError: onReleaseUserFailed,
  mutate: releaseUser,
} = useMutation<{ user: I.User }>(ReleaseUserMutation)
const {
  onDone: onRestrictUserDone,
  onError: onRestrictUserFailed,
  mutate: restrictUser,
} = useMutation<{ status: I.User }>(RestrictUserMutation)

onUsersLoadingFailed((err) => {
  console.log('onUsersLoadingFailed', err)
})

// @ts-expect-error Jevgeni fix it later
onReleaseUserDone(({ data: { user } }) => {
  toast.success(`User "${user.firstname} ${user.lastname}" activated`)
})

onReleaseUserFailed((err) => {
  toast.warning('Activation failed')
  console.log('onReleaseUserFailed | onError:', err)
})

// @ts-expect-error Jevgeni fix it later
onRestrictUserDone(({ data: { user } }) => {
  toast.warning(`User "${user.firstname} ${user.lastname}" deactivated`)
})

onRestrictUserFailed((err) => {
  toast.warning('Deactivation failed')
})

function getRoleBadgeByRoleId(roleId: UserRoleId | null): string {
  return (
    {
      [UserRoleId.SystemUser]: '',
      [UserRoleId.SuperUser]: t('users.page.superUser'),
      [UserRoleId.Administrator]: t('users.page.administrator'),
      [UserRoleId.ProjectOwner]: t('users.page.projectOwner'),
      [UserRoleId.CustomerSupport]: t('users.page.customerSupport'),
    }[roleId as UserRoleId] ?? 'N/A'
  )
}

function getRoleClassesByRoleId(roleId: UserRoleId | null): string {
  return (
    {
      [UserRoleId.SystemUser]: '',
      [UserRoleId.SuperUser]:
        'noselect rounded-md bg-yellow-300 px-2 py-1.5 text-xs font-medium text-yellow-900 ring-1 ring-inset ring-yellow-700/10',
      [UserRoleId.Administrator]:
        'noselect rounded-md bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10',
      [UserRoleId.ProjectOwner]:
        'noselect rounded-md bg-green-50 px-2 py-1.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20',
      [UserRoleId.CustomerSupport]:
        'noselect rounded-md bg-pink-100 px-2 py-1.5 text-xs font-medium text-pink-900 ring-1 ring-inset ring-pink-500/20',
    }[roleId as UserRoleId] ?? ''
  )
}

function onProjectClickedWithPressedAlt(projectKey: string): void {
  userStore.setCurrentProjectByKey(projectKey)
  router.push({ path: `/${projectKey}/settings` })
}

function onProjectClicked(projectKey: string): void {

  router.push({ path: `/${projectKey}/chats` })
}

function onAddUserButtonClicked(): void {
  router.push({ path: `/users/add` })
}


const title = computed(() => {
  const projectName = projectSelected?.value?.title || ''
  const userId = route.params.userId || null
  const user = (data.value?.users ?? []).find((u) => u.id === Number(userId)) || null
  const userName = user ? `${user.firstname} ${user.lastname}` : ''

  const action =
    route.name === 'add-user-modal'
      ? t('common.meta.adding')
      : userId
        ? `${t('common.meta.editing')} - ${userName}`
        : ''

  const titleParts = [projectName, t('navigation.users')]
  if (action) titleParts.push(`- ${action}`)

  return titleParts.join(' | ')
})

const users = computed<User[]>(() => {
  if (isNull(projectSelected.value)) {
    return sortBy(data.value?.users ?? [], ['firstname', 'lastname'])
  }

  return sortBy(data.value?.users ?? [], (user) => {
    if (isNull(user.projects)) {
      return 1
    }

    const selectedProject = user.projects.find(({ id }) => id === projectSelected.value?.id)

    return isUndefined(selectedProject) ? 1 : -1
  })
})

function formatDateToDDMMYYYYHHmm(date: string): string {
  return dayjs(date).format('DD MMM YYYY HH:mm')
}

usePageTitle(title)
</script>
<template>
  <main>
    <CrdWrapper v-if="!isLoading">
      <div class="sm:flex sm:items-center">
        <div class="flex justify-end gap-x-4 w-full">
          <div class="w-max">
            <button
              type="button"
              class="inline-flex items-center w-full mr-2 gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              @click="onAddUserButtonClicked"
            >
              <UserPlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
              {{ t('users.page.addUser') }}
            </button>
          </div>
        </div>
      </div>
      <ul role="list" class="divide-y divide-gray-100 w-full space-y-2">
        <CrdBlock
          v-for="user in users"
          :key="user.id as number"
          :custom-background="true"
          :class-custom="{
            'justify-between h-20 justify-center': true,
            [
              (isNull(projectSelected)
                ? isNull(user.projects) && ![UserRoleId.SuperUser, UserRoleId.Administrator].includes(user.roleId as any) // TODO: Fix type
                : !isUndefined(user.projects?.find(({ id }) => id === projectSelected?.id))
              ) ? 'bg-purple-50' : 'bg-white']: true,
            }"
        >
          <div class="flex flex-col">
            <span class="text-sm/6 font-semibold text-gray-900">
              {{ user.firstname }} {{ user.lastname }}
              <span :class="[getRoleClassesByRoleId(user.roleId), 'ml-2']">
              {{ getRoleBadgeByRoleId(user.roleId )}}
              </span>
              <span v-if="!isNull(user.restrictedBy)" class="noselect rounded-md bg-red-50 px-2 py-1.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20 ml-2">
                Deactivated
              </span>
              <span v-if="isNull(user.restrictedBy)"  class="noselect rounded-md bg-green-50 px-2 py-1.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 ml-2">
                Activated
              </span>
            </span>

            <div class="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
              <div
                v-for="project in user?.projects ?? []"
                @click.alt.exact="onProjectClickedWithPressedAlt(project.key)"
                @click.exact="() => onProjectClicked(project.key)"
                class="inline-block mr-1 hover:cursor-pointer hover:text-black"
              >
                <p class="whitespace-nowrap" v-if="!isEmpty(user.companyName)">
                 <span class="font-medium">{{ user.companyName }}</span>: {{ project.title }}
                </p>
                <p class="whitespace-nowrap" v-else>
                  {{ project.title }}
                </p>
                <!--<svg viewBox="0 0 2 2" class="size-0.5 fill-current">
                  <circle cx="1" cy="1" r="1" />
                </svg>-->
              </div>
            </div>
            <!--<span
              v-if="user.companyName"
              class="text-center w-auto mt-1 gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
            >
              {{ user.companyName }}
            </span>-->
          </div>
          <div class="flex flex-col w-36 items-end items-center justify-center">
            <div class="flex flex-none items-center gap-x-4">
              <RouterLink
                :to="`/users/${user?.id}/edit`"
                class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
              >
                {{ t('common.buttons.edit') }}
              </RouterLink>
              <Menu as="div" class="relative">
                <MenuButton class="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                  <span class="sr-only">{{ t('projects.page.openOptions') }}</span>
                  <EllipsisVerticalIcon class="size-5" aria-hidden="true" />
                </MenuButton>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
                  >
                    <MenuItem
                      v-if="isNull(user.restrictedBy)"
                      v-slot="{ active }"
                      @click="restrictUser({ userId: user.id as number })"
                    >
                      <span :class="[active ? 'bg-gray-200 outline-none hover:cursor-pointer' : '', 'block pl-6 pl-6 py-2 text-sm/6 text-gray-900']">
                        {{ t('users.page.activate') }}
                      </span>
                    </MenuItem>
                    <MenuItem
                      v-else
                      v-slot="{ active }"
                      @click="releaseUser({ userId: user.id as number })"
                    >
                      <span :class="[active ? 'bg-gray-200 outline-none hover:cursor-pointer' : '', 'block pl-6 pl-6 py-2 text-sm/6 text-gray-900']">
                        {{ t('users.page.deactivate') }}
                      </span>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </CrdBlock>
      </ul>
    </CrdWrapper>
    <div class="pre-footer-text">
      {{ t('users.page.overviewDescription') }}
    </div>
  </main>
  <router-view />
</template>

<style scoped>
.blinking {
  animation: blink 2s infinite;
}
</style>
